import React, { useState, useRef } from "react";
import Image from "next/image";
import styles from "../../styles/shap_of_you.module.scss";
import WelcomeScreenImg from "../../assets/images/WelcomeScreen_dek.png";
import SOYLockUp from "../../assets/images/spl.png";
import { UploadOrScanScreenProps } from "./types";
import GalleryPermissionScreen from "./GalleryPermissionScreen";
import CameraPermissionScreen from "./CameraPermissionScreen";

const UploadOrScanScreen: React.FC<UploadOrScanScreenProps> = ({
  navigateToStep,
  onImageSelect,
  logStep
}) => {
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection (for direct upload when permission already granted)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        if (onImageSelect) {
          onImageSelect(base64String);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle upload button click - check localStorage first
  const handleUploadClick = () => {
    const galleryPermission = localStorage.getItem("galleryPermissionGranted");

    if (galleryPermission === "true") {
      // Permission already granted - directly trigger file input
      fileInputRef.current?.click();
    } else {
      // Permission not granted - show gallery permission modal
      setShowGalleryModal(true);
    }
  };

  // Handle scan face button click - check browser permission AND localStorage first
  const handleScanFaceClick = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });

      if (permissionStatus.state === "granted") {
        const cameraPermission = localStorage.getItem("cameraPermissionGranted");
        if (cameraPermission === "true") {
          navigateToStep("faceScanning");
        } else {
          // Browser granted but app flag missing - show camera modal to confirm
          setShowCameraModal(true);
        }
      } else if (permissionStatus.state === "prompt") {
        // Need to request permission via modal
        setShowCameraModal(true);
      } else if (permissionStatus.state === "denied") {
        // Previously denied
        navigateToStep("permissionDenied");
      }
    } catch (error) {
      // Permissions API not supported - fall back to localStorage check
      const cameraPermission = localStorage.getItem("cameraPermissionGranted");
      if (cameraPermission === "true") {
        navigateToStep("faceScanning");
      } else {
        setShowCameraModal(true);
      }
    }
  };

  // Handle camera permission granted
  const handleCameraPermissionGranted = () => {
    navigateToStep("faceScanning");
  };

  // Handle gallery permission denied
  const handleGalleryPermissionDenied = () => {
    navigateToStep("permissionDenied");
  };

  // Handle camera permission denied
  const handleCameraPermissionDenied = () => {
    navigateToStep("permissionDenied");
  };

  return (
    <>
      <div className="d-none d-md-block w-100">
        <div className={`${styles.desktop_view_shap_welcome} desktop_view_shap_welcome`}>
          <div className={styles.bgImage}>
            <Image src={WelcomeScreenImg} alt="Welcome Screen" />
          </div>
          <div className={styles.shapeOfYou}>
            <div className={styles.content}>
              <div className={styles.title}>
                <Image src={SOYLockUp} alt="Shape of You Logo" />
              </div>
            </div>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className={styles.button_cont}>
              <button className={styles.ctaBtn} onClick={handleUploadClick}>
                Upload your Image{" "}
                <svg
                  className="ml-1"
                  width="20"
                  height="20"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.5 12H22.5146"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.75 31.5H9C7.80653 31.5 6.66193 31.0259 5.81802 30.182C4.97411 29.3381 4.5 28.1935 4.5 27V9C4.5 7.80653 4.97411 6.66193 5.81802 5.81802C6.66193 4.97411 7.80653 4.5 9 4.5H27C28.1935 4.5 29.3381 4.97411 30.182 5.81802C31.0259 6.66193 31.5 7.80653 31.5 9V18.75"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.5 24L12 16.5C13.392 15.1605 15.108 15.1605 16.5 16.5L21.75 21.75"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 21L22.5 19.5C23.5185 18.5205 24.7095 18.2565 25.821 18.711"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M28.5 33V24"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M33 28.5L28.5 24L24 28.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div>OR</div>
              <button className={styles.ctaBtn} onClick={handleScanFaceClick}>
                Scan Face{" "}
                <svg
                  className="ml-1"
                  width="20"
                  height="20"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.5 4.5H7.5C5.84314 4.5 4.5 5.84314 4.5 7.5V10.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M25.5 4.5H28.5C30.1569 4.5 31.5 5.84314 31.5 7.5V10.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22.5 13.5V16.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5 13.5V16.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5 22.5C13.5 22.5 15 24 18 24C21 24 22.5 22.5 22.5 22.5"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.5 31.5001H7.5C5.84314 31.5001 4.5 30.157 4.5 28.5001V25.5001"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M25.5 31.5001H28.5C30.1569 31.5001 31.5 30.157 31.5 28.5001V25.5001"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Permission Modal */}
      <GalleryPermissionScreen
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        onImageSelect={onImageSelect}
        onPermissionDenied={handleGalleryPermissionDenied}
        logStep={logStep}
      />

      {/* Camera Permission Modal */}
      <CameraPermissionScreen
        show={showCameraModal}
        onHide={() => setShowCameraModal(false)}
        onPermissionGranted={handleCameraPermissionGranted}
        onPermissionDenied={handleCameraPermissionDenied}
        logStep={logStep}
      />
    </>
  );
};

export default UploadOrScanScreen;
